/** The management cockpit (spec §51, §102). */
import { useNavigate } from "react-router-dom";
import type { EChartsOption } from "echarts";

import { Chart } from "@/components/charts/Chart";
import { Disclosure } from "@/components/cards/Disclosure";
import { KpiCard } from "@/components/cards/KpiCard";
import { DataTable, type Column } from "@/components/tables/DataTable";
import { useExecutiveDashboard, useInsights, useVpRanking } from "@/hooks/useAnalytics";
import { bucketLabel, count, inr, pct } from "@/utils/format";

interface VpRow {
  key: string; label: string; rank: number; revenue: number; commission: number;
  payout_ratio_pct: number | null; activities: number; employees: number;
  admissions: number; conversions: number; clients: number;
  revenue_per_employee: number | null; suppressed?: boolean;
}

export function ExecutiveDashboard() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useExecutiveDashboard();
  const ranking = useVpRanking();
  const insights = useInsights();

  if (error) {
    return (
      <div className="card p-6">
        <h2 className="font-display font-semibold">Could not load the dashboard</h2>
        <p className="text-sm text-ink-muted mt-1">
          {(error as Error).message}. Check <code>/api/v1/health</code> for which
          dependency is down.
        </p>
      </div>
    );
  }

  const s = data?.summary;
  const d = data?.comparison?.delta_pct ?? {};
  const prev = data?.comparison?.summary ?? {};
  const trend = (data?.trend ?? []) as Array<Record<string, number | string>>;

  const spark = (key: string) => trend.map((t) => Number(t[key] ?? 0));

  const trendOption: EChartsOption = {
    tooltip: { trigger: "axis" },
    legend: { data: ["Revenue", "Commission", "Admissions"] },
    xAxis: {
      type: "category",
      data: trend.map((t) => bucketLabel(String(t.bucket),
                                        t.granularity as string | undefined)),
      // 22 daily ticks will not fit straight; let ECharts drop alternates
      // rather than overlap them.
      axisLabel: { hideOverlap: true, fontSize: 11 },
    },
    yAxis: [
      { type: "value", name: "₹", axisLabel: { formatter: (v: number) => inr(v) } },
      { type: "value", name: "Admissions" },
    ],
    series: [
      {
        name: "Revenue", type: "line", smooth: true, showSymbol: false,
        areaStyle: { opacity: 0.10 },
        data: trend.map((t) => Number(t.revenue ?? 0)),
      },
      {
        name: "Commission", type: "line", smooth: true, showSymbol: false,
        data: trend.map((t) => Number(t.commission ?? 0)),
      },
      {
        name: "Admissions", type: "bar", yAxisIndex: 1, barMaxWidth: 18,
        itemStyle: { opacity: 0.55 },
        data: trend.map((t) => Number(t.admissions ?? 0)),
      },
    ],
  };

  const vpColumns: Column<VpRow>[] = [
    { key: "rank", header: "#", width: "48px", align: "right" },
    { key: "label", header: "Vice President" },
    { key: "revenue", header: "Revenue", align: "right", render: (r) => inr(r.revenue), value: (r) => r.revenue },
    { key: "commission", header: "Payout", align: "right", render: (r) => inr(r.commission), value: (r) => r.commission },
    {
      key: "payout_ratio_pct", header: "Payout ratio", align: "right",
      render: (r) => (r.payout_ratio_pct == null
        ? <span className="text-ink-faint" title="Sample too small to rank">—</span>
        : pct(r.payout_ratio_pct, 2)),
      value: (r) => r.payout_ratio_pct,
    },
    { key: "admissions", header: "Admissions", align: "right", value: (r) => r.admissions },
    { key: "activities", header: "Activities", align: "right", render: (r) => count(r.activities), value: (r) => r.activities },
    { key: "employees", header: "Employees", align: "right", value: (r) => r.employees },
    { key: "clients", header: "Clients", align: "right", value: (r) => r.clients, hidden: true },
  ];

  return (
    <div className="space-y-5">
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(198px, 1fr))" }}>
        <KpiCard loading={isLoading} label="Hospital revenue" value={inr(s?.revenue)}
                 previous={inr(prev.revenue)} deltaPct={d.revenue} sparkline={spark("revenue")}
                 footnote="gross_amount at lead grain" />
        <KpiCard loading={isLoading} label="Referral payout" value={inr(s?.commission_approved)}
                 previous={inr(prev.commission_approved)} deltaPct={d.commission_approved}
                 higherIsBetter={false} sparkline={spark("commission")}
                 footnote="approved, primary credit row only" />
        <KpiCard loading={isLoading} label="Payout ratio" value={pct(s?.payout_ratio_pct, 2)}
                 deltaPct={d.payout_ratio_pct} higherIsBetter={false}
                 footnote="cost of earning the revenue beside it" />
        <KpiCard loading={isLoading} label="Admissions" value={count(s?.admissions)}
                 previous={count(prev.admissions)} deltaPct={d.admissions}
                 sparkline={spark("admissions")} />
        <KpiCard loading={isLoading} label="Conversion" value={pct(s?.conversion_pct)}
                 deltaPct={d.conversion_pct}
                 previous={s?.information ? `${count(s.information)} received` : undefined}
                 footnote="admissions ÷ information received" />
        <KpiCard loading={isLoading} label="Activities" value={count(s?.activities)}
                 previous={count(prev.activities)} deltaPct={d.activities}
                 sparkline={spark("activities")}
                 coveragePct={data?.quality?.unbridged_activity_pct == null
                   ? null : 100 - data.quality.unbridged_activity_pct} />
        <KpiCard loading={isLoading} label="Clients engaged" value={count(s?.unique_clients)}
                 previous={count(prev.unique_clients)} deltaPct={d.unique_clients} />
        <KpiCard loading={isLoading} label="Follow-ups missed" value={count(s?.followups_missed)}
                 deltaPct={d.followups_missed} higherIsBetter={false}
                 coveragePct={s?.followup_compliance_pct == null ? null : 100}
                 footnote="known outcomes only" />
      </div>

      <Disclosure quality={data?.quality} metadata={data?.metadata} />

      <div className="card p-4">
        <h2 className="font-display font-semibold text-[17px] mb-1">Revenue and conversion</h2>
        <p className="text-[12.5px] text-ink-muted mb-3">
          Revenue counted on admission date; commission on its own clock.
        </p>
        <Chart option={trendOption} height={300} loading={isLoading} />
      </div>

      <div>
        <h2 className="font-display font-semibold text-[17px] mb-2">VP performance</h2>
        <DataTable<VpRow>
          rows={(ranking.data ?? []) as unknown as VpRow[]}
          columns={vpColumns}
          rowKey={(r) => r.key}
          onRowClick={(r) => navigate(`/employees/${r.key}`)}
          exportName="vp-performance"
          loading={ranking.isLoading}
          footnote="Manager and VP are the same person on 50.2% of hierarchy rows; ratios are suppressed below the ranking sample floor."
        />
      </div>

      <div>
        <h2 className="font-display font-semibold text-[17px] mb-2">Management insights</h2>
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
          {(insights.data?.items ?? []).slice(0, 6).map((insight) => (
            <article key={insight.id} className="card p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className={`chip ${
                  insight.kind === "opportunity"
                    ? "border-good/30 bg-good-soft text-good"
                    : "border-bad/30 bg-bad-soft text-bad"
                }`}>
                  {insight.kind}
                </span>
                <span className="label">{insight.priority}</span>
              </div>
              <h3 className="font-display font-semibold text-[15px] leading-snug">{insight.title}</h3>
              <p className="text-[13px] text-ink-soft leading-snug">{insight.body}</p>
              <ul className="text-[12px] text-ink-muted font-mono space-y-0.5 border-t border-rule pt-2">
                {insight.evidence.slice(0, 3).map((e) => (
                  <li key={e.label} className="flex justify-between gap-3">
                    <span>{e.label}</span>
                    <span className="tnum">
                      {e.unit === "INR" ? inr(Number(e.value)) : String(e.value ?? "—")}
                      {e.unit && e.unit !== "INR" ? ` ${e.unit}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-[12.5px] font-medium text-petrol">{insight.recommended_action}</p>
            </article>
          ))}
          {insights.data?.items.length === 0 && (
            <p className="text-sm text-ink-muted">No insights for this scope yet.</p>
          )}
        </div>
        <p className="text-[11.5px] text-ink-faint mt-2">
          Generated by deterministic rules from measured metrics — not machine learning.
        </p>
      </div>
    </div>
  );
}
